import {
  Group, Mesh, BoxGeometry, CylinderGeometry, ConeGeometry,
  MeshLambertMaterial, MeshPhongMaterial, Scene, Vector3, Object3D,
} from 'three';
import { IEnemy } from './IEnemy';

const ATTACK_RANGE    = 2000;
const ATTACK_COOLDOWN = 3.0;
const DAMAGE          = 15;

/**
 * 軍艦モデル（駆逐艦風、全長約80m相当）
 */
function buildVesselModel(): { root: Group; turretHead: Object3D } {
  const root = new Group();

  const matHull    = new MeshLambertMaterial({ color: 0x5a5a5a });
  const matDeck    = new MeshLambertMaterial({ color: 0x4a4a44 });
  const matBridge  = new MeshLambertMaterial({ color: 0x666660 });
  const matDark    = new MeshLambertMaterial({ color: 0x333333 });
  const matBarrel  = new MeshPhongMaterial({ color: 0x3a3a3a, shininess: 60 });
  const matRadome  = new MeshLambertMaterial({ color: 0xcccccc });

  // ── 船体 ─────────────────────────────────────────────
  const hull = new Mesh(new BoxGeometry(12, 5, 60), matHull);
  hull.position.set(0, -1, 0);
  root.add(hull);

  // 船首（先細り）
  const bow = new Mesh(new ConeGeometry(6, 18, 4), matHull);
  bow.rotation.x = Math.PI / 2;
  bow.rotation.y = Math.PI / 4;
  bow.position.set(0, -1, -38);
  root.add(bow);

  // 甲板
  const deck = new Mesh(new BoxGeometry(11, 0.5, 55), matDeck);
  deck.position.set(0, 1.8, 2);
  root.add(deck);

  // ── 艦橋 ─────────────────────────────────────────────
  const bridge = new Mesh(new BoxGeometry(7, 6, 10), matBridge);
  bridge.position.set(0, 5, 5);
  root.add(bridge);

  // 艦橋窓
  const windows = new Mesh(new BoxGeometry(7.2, 1.2, 10.2), matDark);
  windows.position.set(0, 7, 5);
  root.add(windows);

  // マスト
  const mast = new Mesh(new CylinderGeometry(0.3, 0.4, 10, 6), matDark);
  mast.position.set(0, 13, 5);
  root.add(mast);

  // レーダードーム
  const radome = new Mesh(new CylinderGeometry(2.5, 2.5, 0.8, 12), matRadome);
  radome.position.set(0, 18.5, 5);
  root.add(radome);

  // ── 前部主砲（旋回砲塔） ─────────────────────────────
  const turretHead = new Group();
  turretHead.position.set(0, 2.5, -16);
  root.add(turretHead);

  const turretBase = new Mesh(new CylinderGeometry(2.8, 3.2, 1.5, 10), matDark);
  turretHead.add(turretBase);

  const turretBody = new Mesh(new BoxGeometry(4.5, 2.0, 5), matHull);
  turretBody.position.set(0, 1.5, 0);
  turretHead.add(turretBody);

  // 主砲身
  const barrel = new Mesh(new CylinderGeometry(0.4, 0.4, 8, 8), matBarrel);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 1.8, -5.5);
  turretHead.add(barrel);

  // ── 後部対空砲 ──────────────────────────────────────
  for (const sx of [-1, 1]) {
    const aa = new Mesh(new CylinderGeometry(0.2, 0.2, 3, 6), matBarrel);
    aa.rotation.x = Math.PI / 2;
    aa.position.set(sx * 4, 3, 18);
    root.add(aa);

    const aaBase = new Mesh(new CylinderGeometry(1, 1, 1, 8), matDark);
    aaBase.position.set(sx * 4, 2.3, 18);
    root.add(aaBase);
  }

  // ── 煙突 ────────────────────────────────────────────
  const funnel = new Mesh(new BoxGeometry(4, 4, 4), matDark);
  funnel.position.set(0, 5, 16);
  root.add(funnel);

  const funnelTop = new Mesh(new CylinderGeometry(1.5, 1.2, 2, 8), matDark);
  funnelTop.position.set(0, 8, 16);
  root.add(funnelTop);

  return { root, turretHead };
}

export class Vessel implements IEnemy {
  readonly group: Group;
  private turretHead: Object3D;
  private hp = 150;
  private attackTimer = 0;
  isAlive = true;
  hitRadius = 40;
  readonly isGroundUnit = true;

  /** EnemyManagerが設定する弾丸発射コールバック */
  onFireBullet: ((from: Vector3, dir: Vector3) => void) | null = null;

  constructor(private scene: Scene, pos: [number, number, number]) {
    const { root, turretHead } = buildVesselModel();
    this.group = root;
    this.turretHead = turretHead;
    this.group.position.set(pos[0], pos[1], pos[2]);
    scene.add(this.group);
  }

  get position(): Vector3 { return this.group.position; }

  takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.isAlive = false;
      this.scene.remove(this.group);
    }
    return !this.isAlive;
  }

  /** @returns ダメージ量 */
  update(delta: number, playerPos: Vector3): number {
    if (!this.isAlive) return 0;

    this.attackTimer = Math.max(0, this.attackTimer - delta);
    const dist = this.group.position.distanceTo(playerPos);

    if (dist < ATTACK_RANGE) {
      // 砲塔ヘッドをプレイヤー方向に旋回
      const worldPos = this.turretHead.getWorldPosition(new Vector3());
      const targetLocal = playerPos.clone().sub(worldPos);
      this.turretHead.lookAt(worldPos.clone().add(targetLocal));

      if (this.attackTimer <= 0) {
        this.attackTimer = ATTACK_COOLDOWN;

        // 弾丸発射
        if (this.onFireBullet) {
          const dir = playerPos.clone().sub(worldPos).normalize();
          const from = worldPos.clone().addScaledVector(dir, 5);
          this.onFireBullet(from, dir);
        }

        const dmg = dist < 500 ? DAMAGE : Math.round(DAMAGE * (1 - dist / ATTACK_RANGE));
        return Math.max(1, dmg);
      }
    }
    return 0;
  }
}
